import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Clock, User, Edit2, Users } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ClassSchedules() {
  const [schedules, setSchedules] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    instructor: '',
    day_of_week: 'Monday',
    start_time: '09:00',
    end_time: '10:00',
    capacity: 20
  });

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      const [schedulesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/schedules`, { withCredentials: true }),
        axios.get(`${API}/class-bookings`, { withCredentials: true }),
      ]);
      setSchedules(Array.isArray(schedulesRes.data) ? schedulesRes.data : []);
      setBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await axios.delete(`${API}/schedules/${scheduleId}`, { withCredentials: true });
      fetchSchedules();
    } catch (error) {
      console.error('Failed to delete schedule:', error);
    }
  };

  const handleEditSchedule = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name || '',
      instructor: schedule.instructor || '',
      day_of_week: schedule.day_of_week || 'Monday',
      start_time: schedule.start_time || '09:00',
      end_time: schedule.end_time || '10:00',
      capacity: schedule.capacity || 20
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await axios.put(`${API}/schedules/${editingSchedule.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/schedules`, formData, { withCredentials: true });
      }
      setIsModalOpen(false);
      setEditingSchedule(null);
      setFormData({ name: '', instructor: '', day_of_week: 'Monday', start_time: '09:00', end_time: '10:00', capacity: 20 });
      fetchSchedules();
    } catch (error) {
      console.error('Failed to save schedule:', error);
      alert(error.response?.data?.detail || 'Failed to save schedule');
    }
  };

  const groupedSchedules = dayOrder.reduce((acc, day) => {
    acc[day] = schedules.filter(s => s.day_of_week === day);
    return acc;
  }, {});

  const getScheduleBookings = (scheduleId) =>
    bookings.filter((booking) => booking.schedule_id === scheduleId);

  return (
    <DashboardLayout>
      <Header title="Class Schedules" />
      <div className="p-8" data-testid="schedules-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Weekly Schedule</h2>
            <p className="text-sm text-zinc-500 mt-1">Manage gym classes and sessions</p>
          </div>
          <Button
            onClick={() => {
              setEditingSchedule(null);
              setFormData({ name: '', instructor: '', day_of_week: 'Monday', start_time: '09:00', end_time: '10:00', capacity: 20 });
              setIsModalOpen(true);
            }}
            className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm"
            data-testid="add-schedule-btn"
          >
            <Plus className="w-4 h-4 mr-2" strokeWidth={2} />
            Add Class
          </Button>
        </div>

        {/* Schedule Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {dayOrder.map((day) => (
              <div key={day} className="bg-zinc-900/50 border border-white/10 rounded-sm">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-zinc-400">{day}</h3>
                </div>
                <div className="p-4 space-y-3">
                  {groupedSchedules[day]?.length > 0 ? (
                    groupedSchedules[day].map((schedule, index) => {
                      const scheduleBookings = getScheduleBookings(schedule.id);
                      return (
                      <div
                        key={schedule.id}
                        className="p-3 bg-zinc-900/50 border border-white/10 rounded-sm hover:border-white/20 transition-colors group"
                        data-testid={`schedule-card-${day.toLowerCase()}-${index}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-sm font-medium text-white">{schedule.name}</h4>
                          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-all">
                            <button
                              onClick={() => handleEditSchedule(schedule)}
                              className="p-1 rounded-sm hover:bg-zinc-700/60 text-zinc-500 hover:text-white transition-all"
                              data-testid={`edit-schedule-${schedule.id}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="p-1 rounded-sm hover:bg-red-500/10 text-zinc-500 hover:text-red-400 transition-all"
                              data-testid={`delete-schedule-${schedule.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-2">
                          <Clock className="w-3 h-3" strokeWidth={1.5} />
                          <span>{schedule.start_time} - {schedule.end_time}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                          <User className="w-3 h-3" strokeWidth={1.5} />
                          <span>{schedule.instructor}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="rounded-sm text-xs bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30">
                            {schedule.enrolled}/{schedule.capacity} enrolled
                          </Badge>
                        </div>
                        <div className="mt-4 border-t border-white/10 pt-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-7 w-7 items-center justify-center rounded-sm bg-[#D4AF37]/10 text-[#D4AF37]">
                                <Users className="h-3.5 w-3.5" strokeWidth={1.7} />
                              </div>
                              <div>
                                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                                  Booking Masuk
                                </p>
                                <p className="text-[11px] text-zinc-600">
                                  Kelola detail di halaman booking
                                </p>
                              </div>
                            </div>
                            <Link
                              to="/class-bookings"
                              className="rounded-sm border border-white/10 bg-black/30 px-2 py-1 text-[11px] font-semibold text-zinc-300 transition-colors hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                            >
                              {scheduleBookings.length} booking
                            </Link>
                          </div>
                        </div>
                      </div>
                    )})
                  ) : (
                    <p className="text-xs text-zinc-600 text-center py-4">No classes scheduled</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Class Modal */}
        <Dialog
          open={isModalOpen}
          onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) {
              setEditingSchedule(null);
            }
          }}
        >
          <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">{editingSchedule ? 'Edit Class' : 'Add New Class'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Class Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Morning Yoga"
                  className="bg-zinc-800 border-white/10 text-white rounded-sm"
                  required
                  data-testid="schedule-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Instructor</Label>
                <Input
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  placeholder="e.g., Sarah Miller"
                  className="bg-zinc-800 border-white/10 text-white rounded-sm"
                  required
                  data-testid="schedule-instructor-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Day</Label>
                <Select value={formData.day_of_week} onValueChange={(value) => setFormData({ ...formData, day_of_week: value })}>
                  <SelectTrigger className="bg-zinc-800 border-white/10 text-white rounded-sm" data-testid="schedule-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    {dayOrder.map(day => (
                      <SelectItem key={day} value={day}>{day}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Start Time</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    required
                    data-testid="schedule-start-time"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="bg-zinc-800 border-white/10 text-white rounded-sm"
                    required
                    data-testid="schedule-end-time"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Capacity</Label>
                <Input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                  className="bg-zinc-800 border-white/10 text-white rounded-sm"
                  required
                  min={1}
                  data-testid="schedule-capacity-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="flex-1 border-white/10 text-white hover:bg-zinc-800 rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm" data-testid="schedule-submit-btn">
                  {editingSchedule ? 'Update Class' : 'Add Class'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}


